import api from '@/lib/api';
import { getCookie } from 'cookies-next';
import { ContextType, ContextObject } from './ContextManager';
import moment from 'moment';
import { assertApiBodyOk } from '@/utils/apiBusinessError';

/**
 * Context Data Service
 * 
 * This service handles fetching specific information for different context types
 * (Incidents, Audits, Committee Meetings, Grievances, etc.) based on their IDs.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Base interface for all context data responses
 */
export interface BaseContextData {
  id: string | number;
  title?: string;
  description?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  facility_id?: string | number;
  facility_name?: string;
}

/**
 * Incident-specific data structure
 */
export interface IncidentData extends BaseContextData {
  incident_type?: string;
  incident_sub_type_name?: string;
  incident_date?: string;
  incident_date_time?: string;
  incident_reported_date_time?: string;
  resident_name?: string;
  location?: string;
  severity?: string;
  status_id?: number;
  investigation_status?: string;
  reported_by?: string;
  reported_date?: string;
  description?: string;
  immediate_actions?: string;
  follow_up_required?: boolean;
  avoidable_flag?: boolean;
  avoidability_comments?: string;
  modified_by?: number;
  modified_by_name?: string;
  modified_datetime?: string;
}

/**
 * Audit-specific data structure
 */
export interface AuditData extends BaseContextData {
  facility_audit_id?: number;
  form_wizard_id?: number;
  form_section_id?: string;
  company_id?: number;
  company_facility_id?: number;
  audit_category_id?: number;
  audit_category?: string;
  audit_type_id?: number;
  audit_type?: string;
  audit_title?: string;
  assigned_user_id?: number;
  assigned_user?: string;
  assigned_role_id?: number;
  assigned_role?: string;
  assigned_committee_id?: number;
  assigned_committee?: string;
  assigned_datetime?: string;
  due_date?: string;
  priority_id?: number;
  priority?: string;
  active_flag?: boolean;
  modified_by?: number;
  modified_by_name?: string;
  modified_datetime?: string;
  completion_status_id?: number;
  status?: string;
  // Legacy fields for backward compatibility
  audit_date?: string;
  auditor_name?: string;
  facility_name?: string;
  status_id?: number;
  audit_status?: string;
  completion_date?: string;
  score?: number;
  total_score?: number;
  compliance_percentage?: number;
}

/**
 * Committee Meeting-specific data structure
 */
export interface CommitteeMeetingData extends BaseContextData {
  meeting_type?: string;
  meeting_date?: string;
  meeting_time?: string;
  location?: string;
  chairperson?: string;
  attendees?: string[];
  agenda_items?: string[];
  status_id?: number;
  meeting_status?: string;
  minutes?: string;
  action_items?: string[];
  // Additional committee meeting fields
  committee_meeting_id?: number;
  meeting_title?: string;
  meeting_location?: string;
  meeting_attendance_names?: string;
  meeting_absentee_names?: string;
  meeting_facilitator_id?: string;
  meeting_facilitator_name?: string;
  meeting_recorder_id?: string;
  meeting_recorder_name?: string;
  meeting_agenda?: string;
  next_meeting_info?: string;
  modified_by?: number;
  modified_by_name?: string;
  modified_datetime?: string;
}

/**
 * Grievance-specific data structure
 */
export interface GrievanceData extends BaseContextData {
  facility_grievance_response_id?: number;
  facility_grievance_id?: number;
  company_id?: number;
  company_facility_id?: number;
  form_wizard_id?: number;
  form_section_id?: number;
  section_title?: string;
  response?: string;
  response_by?: number;
  completion_status_id?: number;
  completed_datetime?: string;
  form_responses?: any[];
  grievance_category_id?: number;
  grievance_category_name?: string;
  grievance_type_id?: number;
  grievance_type_name?: string;
  grievance_title?: string;
  assigned_user_id?: number;
  assigned_user_name?: string;
  assigned_role_id?: number;
  assigned_role_name?: string;
  assigned_committee_id?: number;
  assigned_committee_name?: string;
  assigned_datetime?: string;
  due_date?: string;
  priority_id?: number;
  priority_name?: string;
  active_flag?: boolean;
  modified_by?: number;
  modified_by_name?: string;
  modified_datetime?: string;
  // Legacy fields for backward compatibility
  grievance_type?: string;
  grievance_date?: string;
  complainant_name?: string;
  respondent_name?: string;
  status_id?: number;
  grievance_status?: string;
  priority?: string;
  resolution_date?: string;
  resolution_summary?: string;
  assigned_to?: string;
}

/**
 * Union type for all possible context data
 */
export type ContextData = IncidentData | AuditData | CommitteeMeetingData | GrievanceData;

/**
 * API response wrapper
 */
export interface ContextDataResponse<T = ContextData> {
  data: T;
  message?: string;
  status: 'success' | 'error';
  error?: string;
}

// ============================================================================
// API ENDPOINT MAPPINGS
// ============================================================================

/**
 * Maps context types to their corresponding API endpoints
 */
const CONTEXT_API_ENDPOINTS: Record<ContextType, string> = {
  'Incident': '/v1/facility_incident_response/by_facility',
  'Audit': '/v1/facility_audit/by_facility_id',
  'Committee Meeting': '/v1/committee_meeting/by_facility',
  'Grievance': '/v1/facility_grievance_response/by_facility_grievance_id',
  'Training': '/v1/training/get_by_id',
  'Policy': '/v1/policy/get_by_id',
  'Environmental Service': '/v1/environmental_service/get_by_id',
  'Facility Assessment': '/v1/facility_assessment/get_by_id',
};

// ============================================================================
// CONTEXT DATA SERVICE CLASS
// ============================================================================

export class ContextDataService {
  /**
   * Fetch context-specific data by type and ID
   * 
   * @param contextType - The type of context (Incident, Audit, etc.)
   * @param contextId - The unique identifier for the context
   * @returns Promise containing the context data
   */
  static async getContextData<T extends ContextData = ContextData>(
    contextType: ContextType,
    contextId: string | number,
    facilityId?: string | number
  ): Promise<T> {
    try {
      const endpoint = CONTEXT_API_ENDPOINTS[contextType];
      
      console.log(`🔍 ContextDataService: Fetching ${contextType} data for ID ${contextId}`);
      console.log(`🔍 Using endpoint: ${endpoint}`);
      
      if (!endpoint) {
        throw new Error(`No API endpoint defined for context type: ${contextType}`);
      }

      const params: any = {
        p_company_id: getCookie('company_id'),
        p_company_facility_id: facilityId || getCookie('company_facility_id'),
        p_logged_in_company_user_id: getCookie('company_user_id'),
      };

      // For incidents, audits, and committee meetings using by_facility endpoints, don't add context_id parameter
      if (contextType === 'Incident' || contextType === 'Audit' || contextType === 'Committee Meeting') {
        // Don't add p_context_id for by_facility endpoints
      } else if (contextType === 'Grievance') {
        // Grievance uses specific parameter name
        params.p_facility_grievance_id = contextId;
      } else {
        params.p_context_id = contextId;
        params.p_context_type = contextType;
      }

      console.log(`🔍 API call parameters:`, params);
      const response = await api.get(endpoint, { params });
      console.log(`🔍 API response received:`, response.data);
      
      assertApiBodyOk(
        response.data,
        response.data.error || 'Failed to fetch context data',
      );

      // Handle incident response structure differently
      if (contextType === 'Incident') {
        // For by_facility endpoint, filter by context_id from the array response
        // Try different possible paths to the incidents array
        let incidents = [];
        if (Array.isArray(response.data.data)) {
          incidents = response.data.data;
        } else if (Array.isArray(response.data)) {
          incidents = response.data;
        } else if (Array.isArray(response.data.results)) {
          incidents = response.data.results;
        }
        
        // Debug: Log the response structure and available fields
        console.log('🔍 Incident response data:', response.data);
        console.log('🔍 Incidents array:', incidents);
        console.log('🔍 Looking for contextId:', contextId);
        if (incidents.length > 0) {
          console.log('🔍 First incident fields:', Object.keys(incidents[0]));
          console.log('🔍 First incident data:', incidents[0]);
        }
        
        // Prioritize facility_incident_response_id as the primary field to match
        const incidentResponse = incidents.find((inc: any) => 
          Number(inc.facility_incident_response_id) === Number(contextId) ||
          Number(inc.facility_incident_id) === Number(contextId) || 
          Number(inc.facility_incident_investigation_id) === Number(contextId) ||
          Number(inc.incident_investigation_id) === Number(contextId)
        );
        
        if (!incidentResponse) {
          console.error('🔍 No incident response found. Available incidents:', incidents.map((inc: any) => ({
            incident_id: inc.incident_id,
            facility_incident_investigation_id: inc.facility_incident_investigation_id,
            incident_investigation_id: inc.incident_investigation_id,
            facility_incident_response_id: inc.facility_incident_response_id,
            facility_incident_id: inc.facility_incident_id
          })));
          throw new Error(`Incident response with ID ${contextId} not found`);
        }
        
        // Extract facility_incident_id from the response
        const facilityIncidentId = incidentResponse.facility_incident_id;
        
        if (!facilityIncidentId) {
          console.warn('🔍 No facility_incident_id found in response, using response data directly');
          return this.transformIncidentResponse(incidentResponse) as T;
        }
        
        console.log('🔍 Found facility_incident_id:', facilityIncidentId, 'from response:', incidentResponse.facility_incident_response_id);
        
        // Fetch the full incident using facility_incident_id with targeted endpoint
        try {
          const facilityIncidentParams: any = {
            p_facility_incident_id: facilityIncidentId,
            p_company_id: getCookie('company_id'),
            p_logged_in_company_user_id: getCookie('company_user_id'),
          };
          
          const facilityIncidentResponse = await api.get('/v1/facility_incident/by_id', {
            params: facilityIncidentParams
          });
          
          // Extract the facility incident from the response
          let facilityIncident = null;
          if (facilityIncidentResponse.data) {
            // Handle different response structures
            if (facilityIncidentResponse.data.data) {
              facilityIncident = facilityIncidentResponse.data.data;
            } else if (Array.isArray(facilityIncidentResponse.data) && facilityIncidentResponse.data.length > 0) {
              facilityIncident = facilityIncidentResponse.data[0];
            } else {
              facilityIncident = facilityIncidentResponse.data;
            }
          }
          
          if (facilityIncident) {
            console.log('🔍 Found facility incident:', facilityIncident);
            // Merge the response data with the facility incident data
            const mergedIncident = {
              ...facilityIncident,
              ...incidentResponse,
              // Keep the response_id as the primary identifier for context
              facility_incident_response_id: incidentResponse.facility_incident_response_id,
            };
            return this.transformIncidentResponse(mergedIncident) as T;
          } else {
            console.warn('🔍 Facility incident not found in response, using response data only');
            return this.transformIncidentResponse(incidentResponse) as T;
          }
        } catch (error) {
          console.error('🔍 Error fetching facility incident, using response data only:', error);
          // Fallback to using just the response data if facility incident fetch fails
          return this.transformIncidentResponse(incidentResponse) as T;
        }
      }

      // Handle grievance response structure differently
      if (contextType === 'Grievance') {
        // Check if response data is empty or error
        const grievanceResponseData = response.data;
        if (Array.isArray(grievanceResponseData) && grievanceResponseData.length === 0) {
          // No response data found, try to fetch basic grievance data
          console.log('🔍 No grievance response data found, fetching basic grievance data...');
          try {
            const grievanceParams: any = {
              p_company_id: getCookie('company_id'),
              p_company_facility_id: facilityId || getCookie('company_facility_id'),
              p_logged_in_company_user_id: getCookie('company_user_id'),
            };
            
            const grievanceResponse = await api.get('/v1/facility_grievance/by_company_facility_id', {
              params: grievanceParams
            });
            
            // Find the specific grievance by ID
            let grievances = [];
            if (Array.isArray(grievanceResponse.data.data)) {
              grievances = grievanceResponse.data.data;
            } else if (Array.isArray(grievanceResponse.data)) {
              grievances = grievanceResponse.data;
            }
            
            const grievance = grievances.find((g: any) => 
              Number(g.facility_grievance_id) === Number(contextId) ||
              Number(g.id) === Number(contextId)
            );
            
            if (grievance) {
              console.log('🔍 Found basic grievance data:', grievance);
              // Transform basic grievance data (without response)
              return this.transformGrievanceData(grievance) as T;
            } else {
              throw new Error(`Grievance with ID ${contextId} not found`);
            }
          } catch (fallbackError) {
            console.error('🔍 Error fetching basic grievance data:', fallbackError);
            throw new Error(`Grievance with ID ${contextId} not found`);
          }
        }
        
        return this.transformGrievanceResponse(grievanceResponseData) as T;
      }

      // Handle audit response structure differently
      if (contextType === 'Audit') {
        // For by_facility_id endpoint, filter by audit_id from the array response
        // Try different possible paths to the audits array
        let audits = [];
        if (Array.isArray(response.data.data)) {
          audits = response.data.data;
        } else if (Array.isArray(response.data)) {
          audits = response.data;
        } else if (Array.isArray(response.data.results)) {
          audits = response.data.results;
        }
        
        // Debug: Log the response structure and available fields
        console.log('🔍 Audit response data:', response.data);
        console.log('🔍 Audits array:', audits);
        console.log('🔍 Looking for auditId:', contextId);
        if (audits.length > 0) {
          console.log('🔍 First audit fields:', Object.keys(audits[0]));
          console.log('🔍 First audit data:', audits[0]);
        }
        
        // Find the specific audit by ID
        const audit = audits.find((aud: any) => 
          aud.facility_audit_id === contextId ||
          aud.audit_id === contextId || 
          aud.id === contextId
        );
        
        if (!audit) {
          console.error('🔍 No audit found. Available audits:', audits.map((aud: any) => ({
            facility_audit_id: aud.facility_audit_id,
            audit_id: aud.audit_id,
            id: aud.id
          })));
          throw new Error(`Audit with ID ${contextId} not found`);
        }
        
        return this.transformAuditResponse(audit) as T;
      }

      // Handle committee meeting response structure differently
      if (contextType === 'Committee Meeting') {
        // For by_facility endpoint, filter by committee_meeting_id from the array response
        // Try different possible paths to the committee meetings array
        let committeeMeetings = [];
        if (Array.isArray(response.data.data)) {
          committeeMeetings = response.data.data;
        } else if (Array.isArray(response.data)) {
          committeeMeetings = response.data;
        } else if (Array.isArray(response.data.results)) {
          committeeMeetings = response.data.results;
        }
        
        // Debug: Log the response structure and available fields
        console.log('🔍 Committee Meeting response data:', response.data);
        console.log('🔍 Committee Meetings array:', committeeMeetings);
        console.log('🔍 Looking for committeeMeetingId:', contextId);
        if (committeeMeetings.length > 0) {
          console.log('🔍 First committee meeting fields:', Object.keys(committeeMeetings[0]));
          console.log('🔍 First committee meeting data:', committeeMeetings[0]);
          console.log('🔍 Meeting agenda data:', committeeMeetings[0].meeting_agenda);
          if (committeeMeetings[0].meeting_agenda) {
            try {
              const agendaData = JSON.parse(committeeMeetings[0].meeting_agenda);
              console.log('🔍 Parsed agenda data:', agendaData);
              if (Array.isArray(agendaData) && agendaData.length > 0) {
                console.log('🔍 First agenda item:', agendaData[0]);
                console.log('🔍 First agenda item sub-items:', agendaData[0].agenda_sub_items);
                if (agendaData[0].agenda_sub_items && agendaData[0].agenda_sub_items.length > 0) {
                  console.log('🔍 First sub-item:', agendaData[0].agenda_sub_items[0]);
                  console.log('🔍 First sub-item keys:', Object.keys(agendaData[0].agenda_sub_items[0]));
                }
              }
            } catch (e) {
              console.log('🔍 Could not parse agenda data:', e);
            }
          }
        }
        
        // Find the specific committee meeting by ID
        const committeeMeeting = committeeMeetings.find((cm: any) => 
          cm.committee_meeting_id === contextId ||
          cm.id === contextId
        );
        
        if (!committeeMeeting) {
          console.error('🔍 No committee meeting found. Available committee meetings:', committeeMeetings.map((cm: any) => ({
            committee_meeting_id: cm.committee_meeting_id,
            id: cm.id
          })));
          throw new Error(`Committee Meeting with ID ${contextId} not found`);
        }
        
        return this.transformCommitteeMeetingResponse(committeeMeeting) as T;
      }

      return response.data.data as T;
    } catch (error) {
      console.error(`Error fetching ${contextType} data for ID ${contextId}:`, error);
      throw error;
    }
  }

  /**
   * Fetch incident-specific data
   * 
   * @param incidentId - The incident ID
   * @returns Promise containing incident data
   */
  static async getIncidentData(incidentId: string | number): Promise<IncidentData> {
    return this.getContextData<IncidentData>('Incident', incidentId);
  }

  /**
   * Fetch audit-specific data
   * 
   * @param auditId - The audit ID
   * @returns Promise containing audit data
   */
  static async getAuditData(auditId: string | number): Promise<AuditData> {
    return this.getContextData<AuditData>('Audit', auditId);
  }

  /**
   * Fetch committee meeting-specific data
   * 
   * @param meetingId - The meeting ID
   * @returns Promise containing meeting data
   */
  static async getCommitteeMeetingData(meetingId: string | number): Promise<CommitteeMeetingData> {
    return this.getContextData<CommitteeMeetingData>('Committee Meeting', meetingId);
  }

  /**
   * Fetch grievance-specific data
   * 
   * @param grievanceId - The grievance ID
   * @returns Promise containing grievance data
   */
  static async getGrievanceData(grievanceId: string | number): Promise<GrievanceData> {
    return this.getContextData<GrievanceData>('Grievance', grievanceId);
  }

  /**
   * Fetch context data using a ContextObject
   * 
   * @param context - The context object containing type and ID
   * @returns Promise containing the context data
   */
  static async getContextDataFromObject<T extends ContextData = ContextData>(
    context: ContextObject
  ): Promise<T> {
    return this.getContextData<T>(context.contextType, context.contextId, context.facilityId);
  }

  /**
   * Batch fetch multiple contexts
   * 
   * @param contexts - Array of context objects
   * @returns Promise containing array of context data
   */
  static async getMultipleContextData(contexts: ContextObject[]): Promise<ContextData[]> {
    const promises = contexts.map(context => 
      this.getContextDataFromObject(context).catch(error => {
        console.error(`Error fetching data for ${context.contextType} ${context.contextId}:`, error);
        return null;
      })
    );

    const results = await Promise.all(promises);
    return results.filter((data): data is ContextData => data !== null);
  }

  /**
   * Get context summary (lightweight data for lists/cards)
   * 
   * @param contextType - The type of context
   * @param contextId - The context ID
   * @returns Promise containing basic context information
   */
  static async getContextSummary(
    contextType: ContextType,
    contextId: string | number
  ): Promise<Pick<ContextData, 'id' | 'title' | 'description' | 'status' | 'created_at'>> {
    try {
      const fullData = await this.getContextData(contextType, contextId);
      return {
        id: fullData.id,
        title: fullData.title,
        description: fullData.description,
        status: fullData.status,
        created_at: fullData.created_at,
      };
    } catch (error) {
      console.error(`Error fetching context summary for ${contextType} ${contextId}:`, error);
      throw error;
    }
  }

  /**
   * Transform incident response data to match IncidentData interface
   * 
   * @param responseData - The raw incident response data
   * @returns Transformed incident data
   */
  private static transformIncidentResponse(responseData: any): IncidentData {
    // Extract data from the JSON response field
    let parsedResponse = {};
    try {
      parsedResponse = responseData.response ? JSON.parse(responseData.response) : {};
    } catch (error) {
      console.error('Error parsing incident response:', error);
    }

    // Helper function to extract field values from the response array
    const extractFieldValue = (data: any, fieldName: string) => {
      if (Array.isArray(data)) {
        const field = data.find((item: any) => 
          item.question_label?.toLowerCase().includes(fieldName.toLowerCase()) ||
          item.question_text?.toLowerCase().includes(fieldName.toLowerCase()) ||
          item.field_name?.toLowerCase().includes(fieldName.toLowerCase())
        );
        return field?.question_response || field?.answer_value;
      }
      return data[fieldName];
    };

    // Extract incident details from response data
    const residentName = extractFieldValue(parsedResponse, 'Resident Name') || 
                        extractFieldValue(parsedResponse, 'Patient Name') ||
                        extractFieldValue(parsedResponse, 'Identifier');
    
    const incidentType = extractFieldValue(parsedResponse, 'Incident Type') || 
                        extractFieldValue(parsedResponse, 'Type of Incident');
    
    const incidentDate = extractFieldValue(parsedResponse, 'Date of Incident') || 
                        extractFieldValue(parsedResponse, 'Incident Date');
    
    const incidentTime = extractFieldValue(parsedResponse, 'Time of Incident') || 
                        extractFieldValue(parsedResponse, 'Incident Time');
    
    const incidentDescription = extractFieldValue(parsedResponse, 'Incident Description') || 
                              extractFieldValue(parsedResponse, 'Description');
    
    const reportedDate = extractFieldValue(parsedResponse, 'Date Reported') || 
                        extractFieldValue(parsedResponse, 'Reported Date');
    
    const reportedTime = extractFieldValue(parsedResponse, 'Time Reported') || 
                        extractFieldValue(parsedResponse, 'Reported Time');
    
    const incidentIdentifier = extractFieldValue(parsedResponse, 'Incident Identifier') || 
                              extractFieldValue(parsedResponse, 'Identifier');
    
    const severity = extractFieldValue(parsedResponse, 'Severity Level') || 
                    extractFieldValue(parsedResponse, 'Severity');
    
    const immediateActions = extractFieldValue(parsedResponse, 'Immediate Action Taken') || 
                            extractFieldValue(parsedResponse, 'Immediate Actions');
    
    const injurySustained = extractFieldValue(parsedResponse, 'Injury Sustained');
    const medicalTreatmentRequired = extractFieldValue(parsedResponse, 'Medical Treatment Required');

    // Helper function to combine date and time
    const combineDateTime = (date: string | undefined, time: string | undefined) => {
      if (!date) return undefined;
      
      // If date is already a full ISO datetime string, use it directly
      if (date.includes('T') && date.includes('Z')) {
        return date;
      }
      
      // If time is also a full ISO datetime string, use it instead
      if (time && time.includes('T') && time.includes('Z')) {
        return time;
      }
      
      // If time is in HH:MM format, combine with date
      if (time && time.match(/^\d{1,2}:\d{2}$/)) {
        return `${date} ${time}`;
      }
      
      // If time is in HH:MM AM/PM format, combine with date
      if (time && time.match(/^\d{1,2}:\d{2}\s*(AM|PM)$/i)) {
        return `${date} ${time}`;
      }
      
      return date; // Return just date if time format is not recognized
    };

    // Use facility_incident_identifier from facility_incident data if available, otherwise fall back to parsed response
    const identifier = responseData.facility_incident_identifier || incidentIdentifier || `Incident #${responseData.facility_incident_response_id}`;
    
    // Use incident_date_time from facility_incident data if available, otherwise use parsed response
    const incidentDateTime = responseData.incident_date_time || combineDateTime(incidentDate, incidentTime);
    
    // Use incident_reported_date_time from facility_incident data if available, otherwise use parsed response
    const reportedDateTime = responseData.incident_reported_date_time || combineDateTime(reportedDate, reportedTime);

    return {
      id: responseData.facility_incident_response_id?.toString() || responseData.id,
      title: identifier,
      description: incidentDescription || 'No description available',
      status: responseData.completion_status || 'Unknown',
      created_at: responseData.completed_datetime || moment().toISOString(),
      updated_at: responseData.completed_datetime || moment().toISOString(),
      facility_id: responseData.company_facility_id,
      facility_name: responseData.facility_name,
      // Use incident_type_name from facility_incident, but if it's "other" or similar, use facility_type_other
      incident_type: (responseData.incident_type_name?.toLowerCase() === 'other' && responseData.facility_type_other) 
        ? responseData.facility_type_other 
        : (responseData.incident_type_name || incidentType),
      // Use incident_sub_type_name, but if it's "other" or similar, use facility_sub_type_other
      incident_sub_type_name: (responseData.incident_sub_type_name?.toLowerCase() === 'other' && responseData.facility_sub_type_other) 
        ? responseData.facility_sub_type_other 
        : responseData.incident_sub_type_name,
      incident_date: incidentDateTime,
      incident_date_time: responseData.incident_date_time, // Store the raw datetime for display
      incident_reported_date_time: responseData.incident_reported_date_time, // Store the raw datetime for display
      resident_name: residentName || (responseData.resident_first_name && responseData.resident_last_name 
        ? `${responseData.resident_first_name} ${responseData.resident_last_name}` 
        : responseData.resident_first_name || responseData.resident_last_name),
      location: extractFieldValue(parsedResponse, 'Location') || 
                extractFieldValue(parsedResponse, 'Location of Incident'),
      severity: severity,
      investigation_status: responseData.completion_status_id === 9828 ? 'Completed' : 
                           responseData.completion_status_id === 9827 ? 'In Progress' : 'Not Started',
      reported_by: responseData.response_by?.toString(),
      reported_date: reportedDateTime,
      immediate_actions: immediateActions,
      follow_up_required: medicalTreatmentRequired === 'Yes' || medicalTreatmentRequired === true,
      avoidable_flag: injurySustained === 'Yes' || injurySustained === true,
      avoidability_comments: immediateActions,
      modified_by: responseData.modified_by,
      modified_by_name: responseData.modified_by_name,
      modified_datetime: responseData.modified_datetime,
    };
  }

  /**
   * Transform basic grievance data (without response) to match GrievanceData interface
   * 
   * @param grievanceData - The raw grievance data
   * @returns Transformed grievance data
   */
  private static transformGrievanceData(grievanceData: any): GrievanceData {
    const transformedData = {
      id: grievanceData.facility_grievance_id?.toString() || grievanceData.id,
      title: grievanceData.grievance_title || `Grievance #${grievanceData.facility_grievance_id}`,
      description: grievanceData.grievance_title || 'No description available',
      status: grievanceData.workflow_status || grievanceData.completion_status || 'In Progress',
      created_at: grievanceData.assigned_datetime || moment().toISOString(),
      updated_at: grievanceData.modified_datetime || moment().toISOString(),
      facility_id: grievanceData.company_facility_id,
      facility_name: grievanceData.facility_name,
      // New fields from API response
      facility_grievance_response_id: grievanceData.facility_grievance_response_id || undefined,
      facility_grievance_id: grievanceData.facility_grievance_id,
      company_id: grievanceData.company_id,
      company_facility_id: grievanceData.company_facility_id,
      form_wizard_id: grievanceData.form_wizard_id,
      form_section_id: grievanceData.form_section_id,
      response: undefined,
      response_by: undefined,
      completion_status_id: grievanceData.completion_status_id,
      completed_datetime: grievanceData.completed_datetime,
      form_responses: [],
      // Grievance-specific fields
      grievance_category_id: grievanceData.grievance_category_id,
      grievance_category_name: grievanceData.grievance_category_name,
      grievance_type_id: grievanceData.grievance_type_id,
      grievance_type_name: grievanceData.grievance_type_name,
      assigned_user_id: grievanceData.assigned_user_id,
      assigned_user_name: grievanceData.assigned_user_name,
      assigned_role_id: grievanceData.assigned_role_id,
      assigned_role_name: grievanceData.assigned_role_name,
      assigned_committee_id: grievanceData.assigned_committee_id,
      assigned_committee_name: grievanceData.assigned_committee_name,
      assigned_datetime: grievanceData.assigned_datetime,
      due_date: grievanceData.due_date,
      priority_id: grievanceData.priority_id,
      priority_name: grievanceData.priority_name,
      active_flag: grievanceData.active_flag,
      modified_by: grievanceData.modified_by,
      modified_by_name: grievanceData.modified_by_name,
      modified_datetime: grievanceData.modified_datetime,
      // Legacy field mappings for backward compatibility
      grievance_type: grievanceData.grievance_type_name,
      grievance_date: grievanceData.assigned_datetime,
      complainant_name: undefined,
      respondent_name: undefined,
      status_id: grievanceData.completion_status_id,
      grievance_status: grievanceData.workflow_status || grievanceData.completion_status || 'In Progress',
      priority: grievanceData.priority_name,
      resolution_date: grievanceData.completed_datetime,
      resolution_summary: undefined,
      assigned_to: grievanceData.assigned_user_name || grievanceData.assigned_role_name || grievanceData.assigned_committee_name,
    };
    
    return transformedData as GrievanceData;
  }

  /**
   * Transform grievance response data to match GrievanceData interface
   * 
   * @param responseData - The raw grievance response data
   * @returns Transformed grievance data
   */
  private static transformGrievanceResponse(responseData: any): GrievanceData {
    // Handle array response (form responses)
    const grievanceData = Array.isArray(responseData) ? responseData[0] : responseData;
    
    // Add defensive check for undefined data
    if (!grievanceData) {
      console.error('Grievance response data is undefined:', responseData);
      throw new Error('No grievance data found in response');
    }
    
    
    // Parse form responses if they exist
    let form_responses = [];
    if (grievanceData.response) {
      try {
        form_responses = JSON.parse(grievanceData.response);
      } catch (error) {
        console.error('Error parsing grievance form responses:', error);
        form_responses = [];
      }
    }

    // Extract key information from form responses
    const grievanceCategory = form_responses.find((r: any) => r.question_label === 'Grievance Category')?.question_response || 'Unknown';
    const grievanceType = form_responses.find((r: any) => r.question_label === 'Grievance Type')?.question_response || 'Unknown';
    const residentName = form_responses.find((r: any) => r.question_label === 'Resident Name')?.question_response || 'Unknown';

    const transformedData = {
      id: grievanceData.facility_grievance_id?.toString() || grievanceData.id,
      title: `Grievance #${grievanceData.facility_grievance_id} - ${grievanceCategory}`,
      description: `${grievanceCategory} - ${grievanceType} (Resident: ${residentName})`,
      status: grievanceData.completed_datetime ? 'Completed' : 'In Progress',
      created_at: grievanceData.assigned_datetime || moment().toISOString(),
      updated_at: grievanceData.modified_datetime || moment().toISOString(),
      facility_id: grievanceData.company_facility_id,
      facility_name: grievanceData.facility_name,
      // New fields from API response
      facility_grievance_response_id: grievanceData.facility_grievance_response_id,
      facility_grievance_id: grievanceData.facility_grievance_id,
      company_id: grievanceData.company_id,
      company_facility_id: grievanceData.company_facility_id,
      form_wizard_id: grievanceData.form_wizard_id,
      form_section_id: grievanceData.form_section_id,
      response: grievanceData.response,
      response_by: grievanceData.response_by,
      completion_status_id: grievanceData.completion_status_id,
      completed_datetime: grievanceData.completed_datetime,
      form_responses: form_responses,
      // Legacy field mappings for backward compatibility
      grievance_type: grievanceType,
      grievance_date: grievanceData.assigned_datetime,
      complainant_name: residentName,
      respondent_name: residentName,
      status_id: grievanceData.completion_status_id,
      grievance_status: grievanceData.completed_datetime ? 'Completed' : 'In Progress',
      priority: 'Standard',
      resolution_date: grievanceData.completed_datetime,
      resolution_summary: `${grievanceCategory} - ${grievanceType}`,
      assigned_to: 'System',
      modified_by: grievanceData.modified_by,
      modified_by_name: grievanceData.modified_by_name,
      modified_datetime: grievanceData.modified_datetime,
    };
    
    
    return transformedData;
  }

  /**
   * Transform committee meeting response data to match CommitteeMeetingData interface
   * 
   * @param responseData - The raw committee meeting response data
   * @returns Transformed committee meeting data
   */
  private static transformCommitteeMeetingResponse(responseData: any): CommitteeMeetingData {
    return {
      id: responseData.committee_meeting_id?.toString() || responseData.id,
      title: responseData.meeting_title || `Committee Meeting #${responseData.committee_meeting_id}`,
      description: responseData.meeting_agenda || responseData.meeting_description || 'Committee Meeting',
      status: responseData.status || 'Scheduled',
      created_at: responseData.created_datetime || moment().toISOString(),
      updated_at: responseData.modified_datetime || moment().toISOString(),
      facility_id: responseData.company_facility_id,
      facility_name: responseData.facility_name,
      // Committee meeting specific fields
      committee_meeting_id: responseData.committee_meeting_id,
      meeting_title: responseData.meeting_title,
      meeting_date: responseData.meeting_date,
      meeting_time: responseData.meeting_time,
      meeting_location: responseData.meeting_location,
      meeting_attendance_names: responseData.meeting_attendance_names,
      meeting_absentee_names: responseData.meeting_absentee_names,
      meeting_facilitator_id: responseData.meeting_facilitator_id,
      meeting_facilitator_name: responseData.meeting_facilitator_name,
      meeting_recorder_id: responseData.meeting_recorder_id,
      meeting_recorder_name: responseData.meeting_recorder_name,
      meeting_agenda: responseData.meeting_agenda,
      action_items: responseData.action_items,
      next_meeting_info: responseData.next_meeting_info,
      meeting_type: responseData.meeting_type,
      modified_by: responseData.modified_by,
      modified_by_name: responseData.modified_by_name,
      modified_datetime: responseData.modified_datetime,
    };
  }

  /**
   * Transform audit response data to match AuditData interface
   * 
   * @param responseData - The raw audit response data
   * @returns Transformed audit data
   */
  private static transformAuditResponse(responseData: any): AuditData {
    return {
      id: responseData.facility_audit_id?.toString() || responseData.id,
      title: responseData.audit_title || `Audit #${responseData.facility_audit_id}`,
      description: `${responseData.audit_category || 'Audit'} - ${responseData.audit_type || 'Unknown Type'}`,
      status: responseData.status || (responseData.active_flag ? 'Active' : 'Inactive'),
      created_at: responseData.assigned_datetime || moment().toISOString(),
      updated_at: responseData.modified_datetime || moment().toISOString(),
      facility_id: responseData.company_facility_id,
      facility_name: responseData.facility_name,
      // New fields from API response
      facility_audit_id: responseData.facility_audit_id,
      form_wizard_id: responseData.form_wizard_id,
      form_section_id: responseData.form_section_id,
      company_id: responseData.company_id,
      company_facility_id: responseData.company_facility_id,
      audit_category_id: responseData.audit_category_id,
      audit_category: responseData.audit_category,
      audit_type_id: responseData.audit_type_id,
      audit_type: responseData.audit_type,
      audit_title: responseData.audit_title,
      assigned_user_id: responseData.assigned_user_id,
      assigned_user: responseData.assigned_user,
      assigned_role_id: responseData.assigned_role_id,
      assigned_role: responseData.assigned_role,
      assigned_committee_id: responseData.assigned_committee_id,
      assigned_committee: responseData.assigned_committee,
      assigned_datetime: responseData.assigned_datetime,
      due_date: responseData.due_date,
      priority_id: responseData.priority_id,
      priority: responseData.priority,
      active_flag: responseData.active_flag,
      modified_by: responseData.modified_by,
      modified_by_name: responseData.modified_by_name,
      modified_datetime: responseData.modified_datetime,
      completion_status_id: responseData.completion_status_id,
      // Legacy field mappings for backward compatibility
      audit_date: responseData.assigned_datetime,
      auditor_name: responseData.assigned_user,
      audit_status: responseData.status,
      completion_date: responseData.due_date,
    };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format context data for display
 * 
 * @param data - The context data
 * @param contextType - The context type
 * @returns Formatted display information
 */
export const formatContextDataForDisplay = (data: ContextData, contextType: ContextType) => {
  const baseInfo = {
    id: data.id,
    title: data.title || `${contextType} #${data.id}`,
    description: data.description,
    status: data.status,
    created_at: data.created_at,
    facility_name: data.facility_name,
  };

  switch (contextType) {
    case 'Incident':
      const incidentData = data as IncidentData;
      return {
        ...baseInfo,
        incident_type: incidentData.incident_type,
        incident_sub_type_name: incidentData.incident_sub_type_name,
        incident_date: incidentData.incident_date,
        incident_date_time: incidentData.incident_date_time,
        incident_reported_date_time: incidentData.incident_reported_date_time,
        resident_name: incidentData.resident_name,
        severity: incidentData.severity,
        investigation_status: incidentData.investigation_status,
        reported_date: incidentData.reported_date,
      };

    case 'Audit':
      const auditData = data as AuditData;
      return {
        ...baseInfo,
        audit_type: auditData.audit_type,
        audit_date: auditData.audit_date,
        auditor_name: auditData.auditor_name,
        score: auditData.score,
        compliance_percentage: auditData.compliance_percentage,
      };

    case 'Committee Meeting':
      const meetingData = data as CommitteeMeetingData;
      return {
        ...baseInfo,
        meeting_type: meetingData.meeting_type,
        meeting_date: meetingData.meeting_date,
        chairperson: meetingData.chairperson,
        attendees_count: meetingData.attendees?.length || 0,
      };

    case 'Grievance':
      const grievanceData = data as GrievanceData;
      return {
        ...baseInfo,
        // Legacy fields
        grievance_type: grievanceData.grievance_type,
        grievance_date: grievanceData.grievance_date,
        complainant_name: grievanceData.complainant_name,
        priority: grievanceData.priority,
        assigned_to: grievanceData.assigned_to,
        // New fields
        grievance_category_name: grievanceData.grievance_category_name,
        grievance_type_name: grievanceData.grievance_type_name,
        assigned_user_name: grievanceData.assigned_user_name,
        assigned_role_name: grievanceData.assigned_role_name,
        assigned_committee_name: grievanceData.assigned_committee_name,
        due_date: grievanceData.due_date,
        priority_name: grievanceData.priority_name,
        assigned_datetime: grievanceData.assigned_datetime,
        completed_datetime: grievanceData.completed_datetime,
        // Include form_wizard_id for form viewing functionality
        form_wizard_id: grievanceData.form_wizard_id,
        facility_grievance_response_id: grievanceData.facility_grievance_response_id,
        facility_grievance_id: grievanceData.facility_grievance_id,
      };

    default:
      return baseInfo;
  }
};

/**
 * Check if context data is complete
 * 
 * @param data - The context data
 * @param contextType - The context type
 * @returns True if data has required fields
 */
export const isContextDataComplete = (data: ContextData, contextType: ContextType): boolean => {
  if (!data || !data.id) return false;

  switch (contextType) {
    case 'Incident':
      const incidentData = data as IncidentData;
      return !!(incidentData.incident_date && incidentData.description);

    case 'Audit':
      const auditData = data as AuditData;
      return !!(auditData.audit_date && auditData.auditor_name);

    case 'Committee Meeting':
      const meetingData = data as CommitteeMeetingData;
      return !!(meetingData.meeting_date && meetingData.chairperson);

    case 'Grievance':
      const grievanceData = data as GrievanceData;
      return !!(grievanceData.grievance_date && grievanceData.description);

    default:
      return !!(data.title || data.description);
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default ContextDataService;
